import React from 'react';
import { Box, Typography, Portal } from 'omaha-design-system';
import type { Stint, StintStats } from './types';
import {
  formatGameTime,
  formatDuration,
  formatPM,
  getLastName,
  hasAnyStats,
  STAT_COLORS,
  TEAM_COLORS,
  ensureVisibleColor,
  TeammateOverlap,
  ROTATION_COLORS,
} from './constants';

const calcDkFp = (s: StintStats): number =>
  s.pts + s.fg3m * 0.5 + s.reb * 1.25 + s.ast * 1.5 + s.stl * 2 + s.blk * 2 - s.tov * 0.5;

interface StintHoverCardProps {
  stint: Stint;
  position: { x: number; y: number };
  teammates: TeammateOverlap[];
  opponents: TeammateOverlap[];
  teamAbbrev: string;
  opponentAbbrev: string;
}

const estimateCardHeight = (
  hasStats: boolean,
  hasFouls: boolean,
  teammateCount: number,
  opponentCount: number,
): number => {
  let h = 80; // header + plus/minus + margin rows
  if (hasStats) h += 120; // shooting line + stat chips + DK FP
  if (hasFouls) h += 40;
  if (teammateCount > 0) h += 30 + Math.ceil(teammateCount / 3) * 28;
  if (opponentCount > 0) h += 30 + Math.ceil(opponentCount / 3) * 28;
  return h;
};

const StintHoverCard: React.FC<StintHoverCardProps> = ({ stint, position, teammates, opponents, teamAbbrev, opponentAbbrev }) => {
  const duration = stint.endTime - stint.startTime;
  const stats = stint.stats;
  const showStats = stats && hasAnyStats(stats);
  const teamColor = ensureVisibleColor(TEAM_COLORS[teamAbbrev] || '#6B7280', 0.35);
  const opponentColor = ensureVisibleColor(TEAM_COLORS[opponentAbbrev] || '#6B7280', 0.35);

  const estimatedHeight = estimateCardHeight(
    !!showStats,
    (stint.foulsTotal ?? 0) > 0,
    teammates.length,
    opponents.length,
  );
  const flipBelow = position.y - estimatedHeight - 8 < 0;

  const cardPositionSx = flipBelow
    ? {
        position: 'fixed',
        zIndex: 9999,
        pointerEvents: 'none',
        transform: 'translate(-50%, 0) translateY(40px)',
      }
    : {
        position: 'fixed',
        zIndex: 9999,
        pointerEvents: 'none',
        transform: 'translate(-50%, -100%) translateY(-8px)',
      };

  const arrowUpSx = {
    position: 'absolute',
    left: '50%',
    top: 0,
    transform: 'translateX(-50%) translateY(-100%)',
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: `8px solid ${ROTATION_COLORS.border.default}`,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -1,
      left: -7,
      width: 0,
      height: 0,
      borderLeft: '7px solid transparent',
      borderRight: '7px solid transparent',
      borderBottom: `7px solid ${ROTATION_COLORS.bg.card}`,
    },
  };

  const arrowDownSx = {
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translateX(-50%) translateY(100%)',
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderTop: `8px solid ${ROTATION_COLORS.border.default}`,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -1,
      left: -7,
      width: 0,
      height: 0,
      borderLeft: '7px solid transparent',
      borderRight: '7px solid transparent',
      borderTop: `7px solid ${ROTATION_COLORS.bg.card}`,
    },
  };

  return (
    <Portal>
      <Box sx={cardPositionSx} style={{ left: position.x, top: position.y }}>
        <Box
          sx={{
            backgroundColor: ROTATION_COLORS.bg.card,
            border: `1px solid ${ROTATION_COLORS.border.default}`,
            borderRadius: 1,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            p: 1.5,
            minWidth: 240,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              pb: 1,
              borderBottom: '1px solid rgba(58, 63, 75, 0.5)',
            }}
          >
            <Typography component="span" sx={{ fontSize: '0.8rem', color: ROTATION_COLORS.text.secondary }}>
              {formatGameTime(stint.startTime)} → {formatGameTime(stint.endTime)}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 500, color: ROTATION_COLORS.text.primary }}>
              {formatDuration(duration)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography component="span" sx={{ fontSize: '0.8rem', color: ROTATION_COLORS.text.secondary }}>
              Plus/Minus
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '0.9rem', fontWeight: 500 }}
              style={{
                color: stint.plusMinus > 0 ? ROTATION_COLORS.pm.positive : stint.plusMinus < 0 ? ROTATION_COLORS.pm.negative : ROTATION_COLORS.pm.neutral,
              }}
            >
              {formatPM(stint.plusMinus)}
            </Typography>
          </Box>

          {stint.marginStart !== undefined && stint.marginEnd !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography component="span" sx={{ fontSize: '0.8rem', color: ROTATION_COLORS.text.secondary }}>
                Margin
              </Typography>
              <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 500 }} style={{ color: ROTATION_COLORS.text.primary }}>
                <Typography component="span" style={{ color: stint.marginStart > 0 ? ROTATION_COLORS.pm.positive : stint.marginStart < 0 ? ROTATION_COLORS.pm.negative : ROTATION_COLORS.pm.neutral }}>
                  {stint.marginStart > 0 ? `+${stint.marginStart}` : stint.marginStart}
                </Typography>
                <Typography component="span" style={{ color: '#4B5563', margin: '0 4px' }}>→</Typography>
                <Typography component="span" style={{ color: stint.marginEnd > 0 ? ROTATION_COLORS.pm.positive : stint.marginEnd < 0 ? ROTATION_COLORS.pm.negative : ROTATION_COLORS.pm.neutral }}>
                  {stint.marginEnd > 0 ? `+${stint.marginEnd}` : stint.marginEnd}
                </Typography>
              </Typography>
            </Box>
          )}

          {/* Stint Stats */}
          {showStats && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(58, 63, 75, 0.5)' }}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: ROTATION_COLORS.text.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 0.5,
                }}
              >
                Stint Stats
              </Typography>

              {/* Shooting line */}
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: ROTATION_COLORS.text.primary,
                  mb: 0.5,
                  '& span': { color: ROTATION_COLORS.text.secondary, margin: '0 4px' },
                }}
              >
                {stats.fga > 0 && (
                  <>{stats.fgm}-{stats.fga} FG</>
                )}
                {stats.fg3a > 0 && (
                  <><span>&bull;</span>{stats.fg3m}-{stats.fg3a} 3P</>
                )}
                {stats.fta > 0 && (
                  <><span>&bull;</span>{stats.ftm}-{stats.fta} FT</>
                )}
              </Typography>

              {/* Counting stats */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {stats.pts > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    }}
                    style={{ color: STAT_COLORS.PTS }}
                  >
                    {stats.pts} PTS
                  </Typography>
                )}
                {stats.reb > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    }}
                    style={{ color: STAT_COLORS.REB }}
                  >
                    {stats.reb} REB
                  </Typography>
                )}
                {stats.ast > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    }}
                    style={{ color: STAT_COLORS.AST }}
                  >
                    {stats.ast} AST
                  </Typography>
                )}
                {stats.stl > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    }}
                    style={{ color: STAT_COLORS.STL }}
                  >
                    {stats.stl} STL
                  </Typography>
                )}
                {stats.blk > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    }}
                    style={{ color: STAT_COLORS.BLK }}
                  >
                    {stats.blk} BLK
                  </Typography>
                )}
                {stats.tov > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    }}
                    style={{ color: STAT_COLORS.TOV }}
                  >
                    {stats.tov} TOV
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 0.75,
                  pt: 0.75,
                  borderTop: `1px solid ${ROTATION_COLORS.border.subtle}`,
                }}
              >
                <Typography component="span" sx={{ fontSize: '0.7rem', color: ROTATION_COLORS.text.secondary, fontWeight: 500 }}>
                  DK Fantasy Pts
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 700, color: ROTATION_COLORS.accent.cyan }}>
                  {calcDkFp(stats).toFixed(1)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Fouls */}
          {stint.foulsTotal != null && stint.foulsTotal > 0 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(58, 63, 75, 0.5)' }}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: ROTATION_COLORS.text.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 0.5,
                }}
              >
                Fouls
              </Typography>
              <Typography
                sx={{ fontSize: '0.8rem', color: ROTATION_COLORS.text.primary }}
                style={{
                  color: stint.foulsTotal >= 4 ? '#EF4444' : stint.foulsTotal >= 2 ? '#F59E0B' : ROTATION_COLORS.text.primary,
                }}
              >
                {stint.foulsInStint ?? 0} in stint &bull; {stint.foulsTotal} total
              </Typography>
            </Box>
          )}

          {/* Teammates */}
          {teammates.length > 0 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(58, 63, 75, 0.5)' }}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: ROTATION_COLORS.text.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 0.5,
                }}
              >
                Teammates
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: 280 }}>
                {teammates.map((tm) => (
                  <Typography
                    component="span"
                    key={tm.name}
                    sx={{
                      fontSize: '0.7rem',
                      color: ROTATION_COLORS.text.secondary,
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit' }} style={{ color: teamColor }}>
                      {getLastName(tm.name)}
                    </Typography>{' '}
                    {formatDuration(tm.overlapSeconds)}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Opponents */}
          {opponents.length > 0 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(58, 63, 75, 0.5)' }}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: ROTATION_COLORS.text.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 0.5,
                }}
              >
                Opponents
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: 280 }}>
                {opponents.map((tm) => (
                  <Typography
                    component="span"
                    key={tm.name}
                    sx={{
                      fontSize: '0.7rem',
                      color: ROTATION_COLORS.text.secondary,
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit' }} style={{ color: opponentColor }}>
                      {getLastName(tm.name)}
                    </Typography>{' '}
                    {formatDuration(tm.overlapSeconds)}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {flipBelow ? <Box sx={arrowUpSx} /> : <Box sx={arrowDownSx} />}
        </Box>
      </Box>
    </Portal>
  );
};

export default StintHoverCard;
